import { safeAppPath } from "./firstScene";

type AuthErrorLike = {
  message?: string;
  code?: string;
  status?: number;
} | null | undefined;

const CODE_MESSAGES: Record<string, string> = {
  invalid_credentials: "E-mail ou senha incorretos.",
  invalid_grant: "E-mail ou senha incorretos.",
  email_not_confirmed: "Confirme seu e-mail antes de entrar. Olhe a caixa de entrada.",
  user_already_exists: "Esse e-mail já tem conta. Tente entrar ou recuperar a senha.",
  email_exists: "Esse e-mail já tem conta. Tente entrar ou recuperar a senha.",
  weak_password: "Essa senha é fraca demais. Use 8+ caracteres, com letra e número.",
  over_email_send_rate_limit: "Muitos e-mails em pouco tempo. Espere um minuto e tente de novo.",
  over_request_rate_limit: "Muitas tentativas. Espere um pouco e tente de novo.",
  otp_expired: "Esse link expirou. Peça um novo e-mail de recuperação.",
  otp_disabled: "Este tipo de login não está disponível.",
  session_not_found: "Sua sessão expirou. Entre de novo.",
  user_not_found: "Não encontramos uma conta com esse e-mail.",
  validation_failed: "Confira o e-mail e a senha e tente de novo.",
  same_password: "A nova senha precisa ser diferente da atual.",
  reauthentication_needed: "Por segurança, entre de novo para trocar a senha.",
};

const MESSAGE_PATTERNS: Array<{ test: RegExp; message: string }> = [
  { test: /invalid login credentials/i, message: "E-mail ou senha incorretos." },
  { test: /email not confirmed/i, message: "Confirme seu e-mail antes de entrar. Olhe a caixa de entrada." },
  { test: /user already registered|already been registered/i, message: "Esse e-mail já tem conta. Tente entrar ou recuperar a senha." },
  { test: /password should be at least/i, message: "Essa senha é curta demais. Use pelo menos 8 caracteres, com letra e número." },
  { test: /password is known to be weak|weak password/i, message: "Essa senha é fraca demais. Use 8+ caracteres, com letra e número." },
  { test: /unable to validate email|invalid format|invalid email/i, message: "Esse e-mail não parece válido." },
  { test: /rate limit|for security purposes/i, message: "Muitas tentativas. Espere um pouco e tente de novo." },
  { test: /token has expired|otp_expired|expired/i, message: "Esse link expirou. Peça um novo e-mail de recuperação." },
  { test: /auth session missing|session.*missing/i, message: "Sua sessão expirou. Entre de novo." },
  { test: /new password should be different/i, message: "A nova senha precisa ser diferente da atual." },
  { test: /signup requires a valid password/i, message: "Digite uma senha válida para criar a conta." },
  { test: /user not found/i, message: "Não encontramos uma conta com esse e-mail." },
  { test: /email rate limit/i, message: "Muitos e-mails em pouco tempo. Espere um minuto e tente de novo." },
  { test: /failed to fetch|networkerror|load failed|err_name_not_resolved/i, message: "Não consegui falar com o servidor. Confira a conexão e tente de novo." },
];

export function translateAuthError(error: AuthErrorLike, fallback = "Não deu certo. Tente de novo."): string {
  if (!error) return fallback;

  const code = error.code?.trim();
  if (code && CODE_MESSAGES[code]) {
    return CODE_MESSAGES[code];
  }

  const raw = error.message?.trim() ?? "";
  if (!raw) return fallback;

  for (const { test, message } of MESSAGE_PATTERNS) {
    if (test.test(raw)) return message;
  }

  // Never surface raw English provider text in the UI.
  return fallback;
}

export type AuthView = "signin" | "signup" | "forgot" | "update-password";

export function resolveAuthView(input: {
  search?: string;
  hash?: string;
  recoveryEvent?: boolean;
}): AuthView {
  if (input.recoveryEvent) return "update-password";

  const search = new URLSearchParams((input.search ?? "").replace(/^\?/, ""));
  const hash = new URLSearchParams((input.hash ?? "").replace(/^#/, ""));
  const mode = search.get("mode") ?? search.get("type") ?? hash.get("type");
  const tab = search.get("tab");

  if (mode === "update-password" || mode === "recovery" || mode === "reset") {
    return "update-password";
  }
  if (mode === "forgot" || mode === "reset-request") {
    return "forgot";
  }
  if (tab === "signup" || tab === "register") {
    return "signup";
  }
  return "signin";
}

export function appRedirectUrl(
  origin = typeof window !== "undefined" ? window.location.origin : "",
  nextPath?: string | null,
): string {
  const path = safeAppPath(nextPath) ?? "/app";
  return `${origin.replace(/\/$/, "")}${path}`;
}

export function authRedirectUrl(
  origin = typeof window !== "undefined" ? window.location.origin : "",
  view: AuthView = "update-password",
): string {
  const base = origin.replace(/\/$/, "");
  if (view === "update-password") {
    return `${base}/auth?mode=update-password`;
  }
  return `${base}/auth`;
}
